import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import { useAppContext } from "../store/AppContext";
import LocationCard from "../components/LocationCard";
import {
  MapPin,
  Landmark,
  UtensilsCrossed,
  BedDouble,
  Compass,
} from "lucide-react";

const LEGACY_BY_MAIN = {
  "du-lich-van-hoa": "culture",
  "am-thuc-local": "food",
  "homestay-khach-san": "stay",
};

const MAIN_CATEGORIES = [
  {
    slug: "all",
    label: "TẤT CẢ",
    icon: Compass,
    description: "Tổng hợp các địa điểm nổi bật ở Đà Lạt — lọc nhanh theo danh mục, loại hình và khu vực.",
  },
  {
    slug: "du-lich-van-hoa",
    label: "VĂN HOÁ",
    icon: Landmark,
    description: "Gợi ý điểm tham quan, kiến trúc, di tích và trải nghiệm văn hoá địa phương.",
  },
  {
    slug: "am-thuc-local",
    label: "ĂN/UỐNG",
    icon: UtensilsCrossed,
    description: "Quán ngon, món đặc trưng và điểm ăn uống được gợi ý theo gu của bạn.",
  },
  {
    slug: "homestay-khach-san",
    label: "HOMESTAY",
    icon: BedDouble,
    description: "Homestay, khách sạn và chỗ ở tiện nghi — dễ lọc theo khu vực và loại hình.",
  },
];

const SUBTYPE_GROUPS = [
  {
    main: "du-lich-van-hoa",
    label: "Văn hoá",
    items: [
      { value: "du-lich", label: "Điểm du lịch" },
      { value: "chua", label: "Chùa" },
      { value: "nha-tho", label: "Nhà thờ" },
      { value: "di-tich", label: "Di tích" },
    ],
  },
  {
    main: "am-thuc-local",
    label: "Ăn / uống local",
    items: [
      { value: "ca-phe-local", label: "Cà phê local" },
      { value: "ca-phe-co-view", label: "Cà phê có view" },
      { value: "quan-lau-nuong", label: "Lẩu nướng" },
      { value: "quan-com-gia-dinh", label: "Cơm gia đình" },
      { value: "quan-an-no", label: "Ăn no" },
      { value: "quan-an-vat", label: "Ăn vặt" },
    ],
  },
  {
    main: "homestay-khach-san",
    label: "Homestay",
    items: [
      { value: "home-co-view", label: "Home có view" },
      { value: "home-tien-nghi", label: "Home tiện nghi" },
      { value: "khach-san", label: "Khách sạn" },
    ],
  },
];

function matchesMainCategory(loc, mainSlug) {
  if (mainSlug === "all") return true;
  const leg = LEGACY_BY_MAIN[mainSlug];
  return leg ? loc.category === leg : false;
}

function slugifySubtype(v) {
  return String(v || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const SORT_OPTIONS = [
  { value: "default", label: "Mặc định" },
  { value: "name", label: "Tên A–Z" },
];

export default function LocationsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { locations = [], loading, error, toggleFavorite, isFavorite } = useAppContext();

  const [mainSlug, setMainSlug] = useState("all");
  const [subSlug, setSubSlug] = useState("all");
  const [wardFilter, setWardFilter] = useState("all");
  const [sortBy, setSortBy] = useState("default");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 9;

  const normalizedRouteCat = (searchParams.get("category") || "").trim().toLowerCase();

  useEffect(() => {
    if (!normalizedRouteCat) {
      setMainSlug("all");
      return;
    }
    const ok = MAIN_CATEGORIES.some((c) => c.slug === normalizedRouteCat);
    setMainSlug(ok ? normalizedRouteCat : "all");
  }, [normalizedRouteCat]);

  useEffect(() => {
    setSubSlug("all");
    setWardFilter("all");
  }, [mainSlug]);

  const activeMeta = useMemo(() => {
    return MAIN_CATEGORIES.find((c) => c.slug === mainSlug) || MAIN_CATEGORIES[0];
  }, [mainSlug]);

  const poolByMain = useMemo(
    () => locations.filter((l) => matchesMainCategory(l, mainSlug)),
    [locations, mainSlug]
  );

  const subTypeOptions = useMemo(() => {
    if (mainSlug === "all") return [];
    const group = SUBTYPE_GROUPS.find((g) => g.main === mainSlug);
    return (group?.items || []).map((it) => ({ slug: it.value, name: it.label }));
  }, [mainSlug]);

  const wardOptions = useMemo(() => {
    const set = new Set();
    poolByMain.forEach((l) => {
      const w = (l.ward || "").trim();
      if (w) set.add(w);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "vi"));
  }, [poolByMain]);

  const filtered = useMemo(() => {
    let list = locations.filter((loc) => {
      if (!matchesMainCategory(loc, mainSlug)) return false;
      if (subSlug !== "all") {
        const leaf = slugifySubtype(loc.categorySlug);
        const selected = slugifySubtype(subSlug);
        if (leaf !== selected) return false;
      }
      if (wardFilter !== "all") {
        const w = (loc.ward || "").trim();
        if (w !== wardFilter) return false;
      }
      return true;
    });

    if (sortBy === "name") {
      list = [...list].sort((a, b) => a.name.localeCompare(b.name, "vi"));
    }
    return list;
  }, [locations, mainSlug, subSlug, wardFilter, sortBy]);

  useEffect(() => {
    setPage(1);
  }, [mainSlug, subSlug, wardFilter, sortBy, normalizedRouteCat]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  }, [filtered.length]);

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const pagedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleCategoryChange = (nextSlug) => {
    setMainSlug(nextSlug);
    if (nextSlug === "all") {
      navigate({ pathname: "/locations", search: "" }, { replace: true });
    } else {
      navigate(
        {
          pathname: "/locations",
          search: `?category=${encodeURIComponent(nextSlug)}`,
        },
        { replace: true }
      );
    }
  };

  const seoTitle =
    mainSlug === "all"
      ? "Địa điểm Đà Lạt – Danh sách đầy đủ"
      : `${activeMeta.label} Đà Lạt – Danh sách địa điểm`;

  const canonicalUrl =
    mainSlug === "all"
      ? "https://longvongdalat.com/locations"
      : `https://longvongdalat.com/locations?category=${encodeURIComponent(mainSlug)}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: seoTitle,
    description: "Danh sách địa điểm du lịch Đà Lạt — lọc theo danh mục, loại hình và khu vực.",
    url: canonicalUrl,
    inLanguage: "vi",
    numberOfItems: filtered.length,
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-white">
        <h1 className="sr-only">
          {mainSlug === "all" ? "Địa điểm" : `${activeMeta.label} – Danh sách địa điểm`}
        </h1>

        {/* FILTERS SECTION */}
        <section className="border-b border-[#ddd4c6] bg-[#fcfbf8]">
          <div className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-8">
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              {/* MAIN CATEGORY */}
              <div>
                <label className="mb-1.5 block">
                  <span className="hidden text-[10px] font-bold uppercase tracking-wider text-[#5f745f] sm:block">
                    Bộ lọc: Danh mục
                  </span>
                </label>
                <select
                  value={mainSlug}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full rounded-lg border border-[#d8cfbf] bg-white px-2 py-2 text-[12px] font-semibold text-[#2f3a31] shadow-sm outline-none transition focus:border-[#5f745f] focus:ring-2 focus:ring-[#dde7dc] sm:px-3 sm:py-2.5 sm:text-sm"
                >
                  {MAIN_CATEGORIES.map(({ slug, label }) => (
                    <option key={slug} value={slug}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* SUB CATEGORY */}
              <div>
                <label className="mb-1.5 block">
                  <span className="hidden text-[10px] font-bold uppercase tracking-wider text-[#5f745f] sm:block">
                    Bộ lọc: Loại địa điểm
                  </span>
                </label>
                <select
                  value={subSlug}
                  onChange={(e) => setSubSlug(e.target.value)}
                  disabled={mainSlug === "all" || subTypeOptions.length === 0}
                  className="w-full rounded-lg border border-[#d8cfbf] bg-white px-2 py-2 text-[12px] font-medium text-[#2f3a31] outline-none transition focus:border-[#5f745f] focus:ring-2 focus:ring-[#dde7dc] disabled:cursor-not-allowed disabled:bg-[#f3f0ea] disabled:text-[#9a9285] sm:px-3 sm:py-2.5 sm:text-sm"
                >
                  <option value="all">Tất cả loại</option>
                  {subTypeOptions.map(({ slug, name }) => (
                    <option key={slug} value={slug}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              {/* WARD FILTER */}
              <div>
                <label className="mb-1.5 block">
                  <span className="hidden text-[10px] font-bold uppercase tracking-wider text-[#5f745f] sm:block">
                    Bộ lọc: Khu vực
                  </span>
                </label>
                <select
                  value={wardFilter}
                  onChange={(e) => setWardFilter(e.target.value)}
                  disabled={wardOptions.length === 0}
                  className="w-full rounded-lg border border-[#d8cfbf] bg-white px-2 py-2 text-[12px] font-medium text-[#2f3a31] outline-none transition focus:border-[#5f745f] focus:ring-2 focus:ring-[#dde7dc] disabled:cursor-not-allowed disabled:bg-[#f3f0ea] disabled:text-[#9a9285] sm:px-3 sm:py-2.5 sm:text-sm"
                >
                  <option value="all">Tất cả khu vực</option>
                  {wardOptions.map((w) => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* SORT + COUNT */}
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-[#5f675b]">Sắp xếp:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="rounded-lg border border-[#d8cfbf] bg-white px-3 py-2 text-sm font-medium text-[#2f3a31] outline-none transition focus:border-[#5f745f]"
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="inline-flex items-center gap-2 rounded-lg border border-[#cfd9cf] bg-[#eef4ed] px-4 py-2.5 text-sm font-semibold text-[#516350]">
                <MapPin size={16} className="text-[#3a6665]" />
                {loading ? "Đang tải…" : `${filtered.length} địa điểm`}
              </div>
            </div>
          </div>
        </section>

        {/* CONTENT */}
        <section className="mx-auto max-w-7xl px-4 py-10 md:px-8 md:py-12">
          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#d9e1d1] border-t-[#3a6665]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#d8cfbf] bg-[#faf7f1] py-20 text-center">
              <MapPin className="mb-4 text-[#b7af9f]" size={48} />
              <p className="text-lg font-bold text-[#2f3a31]">Không có địa điểm phù hợp</p>
              <p className="mt-2 text-sm text-[#6f776b]">Thử thay đổi bộ lọc</p>
              <Link
                to="/locations"
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[#5f745f] px-6 py-3 text-sm font-bold text-white transition hover:bg-[#4f6250]"
              >
                <MapPin size={16} />
                Xem tất cả địa điểm
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {pagedItems.map((item) => (
                  <LocationCard
                    key={item.id}
                    item={item}
                    isFav={typeof isFavorite === "function" ? isFavorite(item.id) : false}
                    onFav={toggleFavorite}
                  />
                ))}
              </div>
 
              {filtered.length > PAGE_SIZE && (
                <div className="mt-10 flex flex-col items-center justify-between gap-4 rounded-2xl border border-[#d8cfbf] bg-[#fcfbf8] px-4 py-4 shadow-[0_8px_24px_rgba(80,70,40,0.05)] sm:flex-row">
                  <div className="text-sm font-semibold text-[#5f675b]">
                    Trang <span className="text-[#2f3a31]">{page}</span> /{" "}
                    <span className="text-[#2f3a31]">{totalPages}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="rounded-xl border border-[#d8cfbf] bg-white px-4 py-2 text-sm font-bold text-[#5f675b] shadow-sm transition hover:border-[#b7c6b5] hover:text-[#5f745f] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Trước
                    </button>

                    <div className="hidden items-center gap-1 sm:flex">
                      {Array.from({ length: totalPages }).slice(0, 50).map((_, i) => {
                        const p = i + 1;
                        const active = p === page;
                        const show =
                          p === 1 ||
                          p === totalPages ||
                          Math.abs(p - page) <= 1;
                        if (!show) return null;
                        return (
                          <button
                            key={p}
                            type="button"
                            onClick={() => setPage(p)}
                            className={`h-10 min-w-10 rounded-xl border px-3 text-sm font-extrabold transition ${
                              active
                                ? "border-[#5f745f] bg-[#5f745f] text-white shadow-md"
                                : "border-[#d8cfbf] bg-white text-[#5f675b] hover:border-[#b7c6b5] hover:text-[#5f745f]"
                            }`}
                            aria-current={active ? "page" : undefined}
                          >
                            {p}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="rounded-xl border border-[#d8cfbf] bg-white px-4 py-2 text-sm font-bold text-[#5f675b] shadow-sm transition hover:border-[#b7c6b5] hover:text-[#5f745f] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Sau
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </MainLayout>
  );
}